import { useEffect } from 'react';

const SERVICE_WORKER_UPDATE_INTERVAL = 60 * 60 * 1000;
const SERVICE_WORKER_PATH = '/service-worker.js';
const SERVICE_WORKER_PRODUCTION_ORIGIN = 'https://b2c.mathdingo.com';

const isProductionServiceWorkerOrigin = () =>
  process.env.NODE_ENV === 'production' &&
  typeof window !== 'undefined' &&
  window.location.origin === SERVICE_WORKER_PRODUCTION_ORIGIN;

const isServiceWorkerScriptFetchError = (error) => {
  const message = String(error?.message || error || '').toLowerCase();

  return (
    message.includes('serviceworker') &&
    message.includes(SERVICE_WORKER_PATH) &&
    (message.includes('failed to update') ||
      message.includes('failed to register') ||
      message.includes('unknown error occurred when fetching the script'))
  );
};

export const useServiceWorker = () => {
  useEffect(() => {
    if (!isProductionServiceWorkerOrigin() || !('serviceWorker' in navigator)) {
      return undefined;
    }

    let updateIntervalId;

    const handleUnhandledRejection = (event) => {
      if (isServiceWorkerScriptFetchError(event.reason)) {
        event.preventDefault();
      }
    };

    const registerServiceWorker = async () => {
      try {
        const registration = await navigator.serviceWorker.register(
          SERVICE_WORKER_PATH,
          {
            scope: '/',
            updateViaCache: 'none',
          },
        );

        updateIntervalId = setInterval(() => {
          registration.update().catch((error) => {
            if (!isServiceWorkerScriptFetchError(error)) {
              console.warn('Service Worker update check failed:', error);
            }
          });
        }, SERVICE_WORKER_UPDATE_INTERVAL);

        registration.addEventListener('updatefound', () => {
          const newWorker = registration.installing;
          if (!newWorker) return;

          newWorker.addEventListener('statechange', () => {
            if (
              newWorker.state === 'installed' &&
              navigator.serviceWorker.controller
            ) {
              console.log('New service worker version available');
            }
          });
        });
      } catch (error) {
        if (!isServiceWorkerScriptFetchError(error)) {
          console.warn('Service Worker registration failed:', error);
        }
      }
    };

    window.addEventListener('unhandledrejection', handleUnhandledRejection);

    if (document.readyState === 'complete') {
      registerServiceWorker();
    } else {
      window.addEventListener('load', registerServiceWorker);
    }

    return () => {
      window.removeEventListener('load', registerServiceWorker);
      window.removeEventListener('unhandledrejection', handleUnhandledRejection);
      if (updateIntervalId) clearInterval(updateIntervalId);
    };
  }, []);
};
